import axios from 'axios'
import CSRF from '@/services/csrf'

import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'

// import Auth from '@/services/auth'
import User from '@/services/users'

Vue.use(Vuex)

/**
 *  @DESC   Cycles the hash when we need to 'clear' local storage
 *  @TODO   Automatically generate a new hash on new builds
 *
 *  Sean Sutton 6/19: Copied from Imby: why are we using this, and why do we
 *                    use persistedstate if only used to save token?
 */
const STORAGE_HASH = 'a8nasd2j'
export const STORAGE_KEY = `changefunder-${STORAGE_HASH}`

const state = {
  user: null,
  globalLoading: true,
  goals: [],
  goal: null,
  projects: [],
  project: null,
  token: null,
  sidebarOpen: false,
}

const types = {
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
}

const mutations = {
  // Update the entire user model
  UPDATE_USER(state, payload) {
    state.user = payload
  },
  UPDATE_USER_TOKEN(state, payload) {
    state.token = payload
  },
  // Log out the user by resetting the state to defaults
  LOGOUT_USER(state) {
    state.token = null
    state.user = null
  },
  ADD_GOAL(state, goal) {
    state.goals.push(goal)
  },
  [types.TOGGLE_SIDEBAR](state) {
    state.sidebarOpen = !state.sidebarOpen
  },
}

const actions = {
  /* Send the auth token in the store to the API to ensure it is valid. */
  validateToken({ state }) {
    if (!state.token) {
      return false
    }
    // eslint-disable-next-line no-undef
    return validateToken(state.token).then(data => data.token_valid)
  },
  /**
   * Refresh the data of the current user in the store.
   * @param {object} vuex - The default Vuex object passed to actions.
   * @returns {Promise} - Promise object representing API call or `null`.
   */
  refreshCurrentUser({ state, commit }) {
    if (!state.token) {
      return null
    }
    return User.api
      .getUser(state.user.id)
      .then(user => {
        commit('UPDATE_USER', user)
        return user
      })
      .catch(() => {
        // do nothing for now
        return null
      })
  },
  /**
   * Check whether user auth is in a bad state, if so force the user to log out.
   *
   * @param {object} vuex - The default Vuex object passed to actions.
   * @param {object} payload - A custom payload for the action.
   * @param {object.object} payload.route - The action checks whether this route requires token authentication.
   *        If yes, then it validates that a token exists and is recognized by the API. If either check fails, the
   *        user is forced to log out.
   *
   * @returns {boolean} - True/false the user was logged out.
   */
  checkShouldLogOut({ state, commit, dispatch }, { route }) {
    // If the route requires token validation, do it
    if (route.matched.some(r => r.meta.validateTokenOnRefresh)) {
      if (!state.token) {
        commit('LOGOUT_USER')
        return true
      }
      return dispatch('validateToken')
        .then(tokenValid => !tokenValid)
        .then(shouldLogOut => {
          if (shouldLogOut) {
            commit('LOGOUT_USER')
            return true
          }
        })
    }
    return false
  },
  /* Updates a maker's project goals in the store. */
  updateGoals({ state, commit }, goal) {
    if (state.goals) {
      commit('ADD_GOAL', goal)
    }
  },
  /* Toggles Sidebar Navigation */
  toggleSidebar({ commit }) {
    commit(types.TOGGLE_SIDEBAR)
  },
}

const getters = {
  userIsLoggedIn: state => {
    return state.token && state.user
  },
  userIsMaker: state => {
    return state.user && state.user.isMaker
  },
  userIsActivator: state => {
    return state.user && state.user.isActivator
  },
  userIsStaff: state => {
    return state.user && state.user.isStaff
  },
  unreadNotificationCount: state => {
    return state.user ? state.user.unreadNotificationCount : 0
  },
  /* Checks whether sidebar is open- returns boolean */
  sidebarOpen: state => {
    return state.sidebarOpen
  },

  /**
   *   Get the axios API client.
   *   This conveniently sets the `baseURL` and `headers` for the API client,
   *   so that we don't have to do this in every function that needs to call
   *   the API.
   *
   *   @param {object} state - The Vuex state, passed to all getters by default.
   *   @param {object} getters - The Vuex getters, passed to all getters by default.
   *   @returns {object} - An instance of the axios API client.
   */
  apiClient: (state, getters) => {
    return axios.create({
      baseURL: `${process.env.VUE_APP_SERVER_BACKEND}/api/`,
      headers: {
        ...CSRF.getHeaders(),
        ...getters.getHeaders,
      },
    })
  },

  /**
   * Get current authentication headers.
   *
   * Reads the `authToken` in the store and generates a properly-formatted
   * 'Authorization' header for use by the axios API client.
   *
   * @param {object} state - The Vuex state, passed to all getters by default.
   * @returns {object} - An object containing the authorization header, if
   *     `state.authToken` has a value. Otherwise, returns an empty object.
   *     Either way, the returned value is suitable for inclusion in an
   *     axios `headers` configuration object.
   */
  getHeaders: state => {
    return state.token ? { Authorization: `Token ${state.token}` } : {}
  },

  /**
   *  Get current logged in user
   *
   * @param {object} state - The vuex state, passed to all getters by default.
   * @returns {object} - An instance of the User class representing the logged in User, or null
   *                     if no user is logged in.
   *
   */
  getUser: state => {
    return state.user
  },
}

export default new Vuex.Store({
  state,
  mutations,
  actions,
  getters,
  plugins: [
    createPersistedState({
      key: STORAGE_KEY,
    }),
  ],
})
